import { FC } from 'react';
import {
    Dialog,
    DialogSurface,
    DialogBody,
    DialogTitle,
    DialogContent,
    DialogActions,
    makeStyles,
    shorthands,
    tokens,
    Text,
} from '@fluentui/react-components';
import { Trash2, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import SyButton from '../Common/SyButton';

export interface SurveyDeleteDialogProps {
    open: boolean;
    surveyName?: string;
    onClose: () => void;
    onConfirm: () => void;
    isDeleting?: boolean;
}

const useStyles = makeStyles({
    dialogSurface: {
        maxWidth: '480px',
        ...shorthands.borderRadius('12px'),
        ...shorthands.overflow('hidden'),
        boxShadow: '0 8px 16px rgba(0, 0, 0, 0.14), 0 0 2px rgba(0, 0, 0, 0.12)',
        ...shorthands.padding(0),
    },
    dialogBody: {
        ...shorthands.padding(0),
        display: 'flex',
        flexDirection: 'column',
    },
    dialogTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        ...shorthands.padding('20px', '20px', '0', '20px'),
    },
    warningIcon: {
        color: 'var(--color-red-500)',
        flexShrink: 0,
    },
    titleText: {
        fontSize: '18px',
        fontWeight: 600,
        color: tokens.colorNeutralForeground1,
    },
    dialogContent: {
        ...shorthands.padding('16px', '20px', '20px', '20px'),
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
    },
    messageText: {
        fontSize: '14px',
        color: tokens.colorNeutralForeground2,
        lineHeight: '20px',
    },
    surveyNameText: {
        fontWeight: 600,
        color: tokens.colorNeutralForeground1,
    },
    warningText: {
        fontSize: '13px',
        color: 'var(--color-red-600)',
        lineHeight: '18px',
    },
    dialogActions: {
        backgroundColor: tokens.colorNeutralBackground3,
        ...shorthands.padding('12px', '16px'),
        ...shorthands.gap('8px'),
        ...shorthands.margin(0),
        width: '100%',
        justifyContent: 'flex-end',
        display: 'flex',
        flexDirection: 'row',
    },
    deleteButton: {
        backgroundColor: 'var(--color-red-500)',
        color: '#ffffff',
        ':hover': {
            backgroundColor: 'var(--color-red-600)',
        },
    },
});

const SurveyDeleteDialog: FC<SurveyDeleteDialogProps> = ({
    open,
    surveyName,
    onClose,
    onConfirm,
    isDeleting = false,
}) => {
    const styles = useStyles();
    const { t } = useTranslation();

    return (
        <Dialog open={open} onOpenChange={(_, data) => { if (!data.open) onClose(); }}>
            <DialogSurface className={styles.dialogSurface}>
                <DialogBody className={styles.dialogBody}>
                    <div className={styles.dialogTitle}>
                        <AlertTriangle size={24} className={styles.warningIcon} />
                        <Text className={styles.titleText}>
                            {t('adminSettings.surveys.deleteDialogTitle', 'Delete Survey')}
                        </Text>
                    </div>
                    <DialogContent className={styles.dialogContent}>
                        <Text className={styles.messageText}>
                            {t('adminSettings.surveys.deleteDialogMessage', 'Are you sure you want to delete this survey?')}
                            {surveyName && (
                                <>
                                    <br />
                                    <span className={styles.surveyNameText}>"{surveyName}"</span>
                                </>
                            )}
                        </Text>
                    </DialogContent>
                    <DialogActions className={styles.dialogActions}>
                        <SyButton
                            appearance="outline"
                            onClick={onClose}
                            disabled={isDeleting}
                        >
                            {t('common.cancel', 'Cancel')}
                        </SyButton>
                        <SyButton
                            appearance="primary"
                            className={styles.deleteButton}
                            onClick={onConfirm}
                            disabled={isDeleting}
                            icon={<Trash2 size={14} />}
                        >
                            {isDeleting
                                ? t('adminSettings.surveys.deleting', 'Deleting...')
                                : t('adminSettings.surveys.deleteButton', 'Delete')}
                        </SyButton>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};

export default SurveyDeleteDialog;
